import { PostHogProvider, usePostHog } from 'posthog-js/react';
import { logger } from 'src/lib/logger';
import { POSTHOG_HOST, POSTHOG_KEY } from '../env.ts';
import { ReactNode, useEffect, useRef } from 'react';
import { useAuth } from './AuthProvider';
import { AnalyticsEvents } from '../hooks/useAnalytics';

const isDevelopment =
  import.meta.env.DEV ||
  window.location.hostname === 'localhost' ||
  window.location.hostname === 'dev.app.aircast.one' ||
  window.location.hostname === '127.0.0.1';

const options = {
  api_host: POSTHOG_HOST,
  opt_out_capturing_by_default: isDevelopment,
};

// Only enable PostHog if both host and key are available and key is not a placeholder
const posthogEnabled = !!POSTHOG_HOST && !!POSTHOG_KEY && POSTHOG_KEY !== 'development';

// Local storage key to track if user has been seen before (for signup tracking)
const SIGNUP_TRACKED_KEY = 'posthog_signup_tracked';

// Component to handle PostHog user identification and signup tracking
const PostHogIdentifier = ({ children }: { children: ReactNode }) => {
  const posthog = usePostHog();
  const { user, isAuthenticated } = useAuth();
  const previousUserId = useRef<string | null>(null);

  useEffect(() => {
    if (!posthog) return;

    if (isAuthenticated && user) {
      // Only identify if user changed
      if (previousUserId.current === user.id) return;
      previousUserId.current = user.id;

      posthog.identify(user.id, {
        email: user.email,
        name: user.name,
      });
      logger.debug('[Analytics] PostHog user identified', { userId: user.id });

      // Track signup for first-time users (using localStorage to detect)
      const signupTrackedUsers = JSON.parse(localStorage.getItem(SIGNUP_TRACKED_KEY) || '{}');
      if (!signupTrackedUsers[user.id]) {
        // First time seeing this user on this device - track signup
        posthog.capture(AnalyticsEvents.USER_SIGNED_UP, {
          auth_provider: 'google',
          signup_source: window.location.pathname,
        });
        posthog.people?.set_once({ signed_up_at: new Date().toISOString() });
        logger.debug('[Analytics] PostHog signup tracked', { userId: user.id });

        // Mark user as tracked
        signupTrackedUsers[user.id] = true;
        localStorage.setItem(SIGNUP_TRACKED_KEY, JSON.stringify(signupTrackedUsers));
      }
    } else if (previousUserId.current) {
      // Only reset if we had a previous user (actual logout)
      previousUserId.current = null;
      posthog.reset();
    }
  }, [posthog, user, isAuthenticated]);

  return <>{children}</>;
};

type Props = { children: ReactNode };

export const AnalyticsProvider = ({ children }: Props) => {
  if (posthogEnabled)
    return (
      <PostHogProvider apiKey={POSTHOG_KEY} options={options}>
        <PostHogIdentifier>{children}</PostHogIdentifier>
      </PostHogProvider>
    );

  return children;
};
